import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * ObjectiveCountdownPattern - Per-nation countdown bars for war objectives
 *
 * Config properties used:
 * - countdownsKey: character/campaign key for persisted countdowns (default: 'objectiveCountdowns')
 * - majorSteps: steps for major objectives (default: 10)
 * - minorStepsRange: [min, max] for minor objectives (default: [4, 6])
 * - tickFrequency: 'weekly' | 'manual'
 * - perNation: whether countdowns are per-nation
 * - maxActivePerNation: { major: 1, minor: 2 }
 *
 * Reusable by Colossus of the Drylands and other frames with objective tracking.
 */
const ObjectiveCountdownPattern = ({
  config,
  character,
  updateCharacter,
  frameStaticData,
  sessionState,
  setSessionState,
  onClose,
}) => {
  const countdownsKey = config?.countdownsKey || 'objectiveCountdowns';
  const majorSteps = config?.majorSteps || 10;
  const minorRange = config?.minorStepsRange || [4, 6];
  const maxActive = config?.maxActivePerNation || { major: 1, minor: 2 };
  const nations = frameStaticData?.nations || {};
  const nationIds = config?.nations || Object.keys(nations);

  // Persisted countdowns: { [nationId]: { objectives: [{ id, name, type, totalSteps, currentStep, completed }] } }
  const allCountdowns = character?.[countdownsKey] || {};

  // UI state for adding new objectives
  const [addingFor, setAddingFor] = useState(null); // nationId
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('minor'); // 'major' | 'minor'
  const [newSteps, setNewSteps] = useState(minorRange[0]);

  /** Get objectives for a nation */
  const getNationObjectives = (nationId) => {
    return allCountdowns[nationId]?.objectives || [];
  };

  /** Count active (non-completed) objectives by type */
  const countActive = (nationId, type) => {
    return getNationObjectives(nationId).filter(o => o.type === type && !o.completed).length;
  };

  /** Save updated countdowns */
  const saveCountdowns = (updated) => {
    updateCharacter({ ...character, [countdownsKey]: updated });
  };

  /** Initialize a nation's objectives from static data if empty */
  const initNation = (nationId) => {
    const nation = nations[nationId];
    if (!nation) return [];
    const objectives = [];
    if (nation.majorObjective) {
      objectives.push({
        id: `${nationId}-major-0`,
        name: nation.majorObjective,
        type: 'major',
        totalSteps: majorSteps,
        currentStep: majorSteps,
        completed: false,
      });
    }
    (nation.minorObjectives || []).forEach((obj, i) => {
      objectives.push({
        id: `${nationId}-minor-${i}`,
        name: obj,
        type: 'minor',
        totalSteps: minorRange[0],
        currentStep: minorRange[0],
        completed: false,
      });
    });
    return objectives;
  };

  /** Ensure nation has objectives (lazy init from static data) */
  const ensureNation = (nationId) => {
    if (allCountdowns[nationId]?.objectives?.length > 0) return allCountdowns;
    const objectives = initNation(nationId);
    return { ...allCountdowns, [nationId]: { objectives } };
  };

  /** Tick a countdown down by 1 */
  const tickDown = (nationId, objectiveId) => {
    const data = ensureNation(nationId);
    const objectives = (data[nationId]?.objectives || []).map(o => {
      if (o.id !== objectiveId || o.completed) return o;
      const next = o.currentStep - 1;
      if (next <= 0) {
        toast.success(`${nations[nationId]?.name || nationId}: "${o.name}" COMPLETED!`, { icon: '🏁', duration: 5000 });
        return { ...o, currentStep: 0, completed: true };
      }
      return { ...o, currentStep: next };
    });
    saveCountdowns({ ...data, [nationId]: { objectives } });
  };

  /** Tick a countdown up by 1 */
  const tickUp = (nationId, objectiveId) => {
    const data = ensureNation(nationId);
    const objectives = (data[nationId]?.objectives || []).map(o => {
      if (o.id !== objectiveId || o.completed) return o;
      const next = Math.min(o.currentStep + 1, o.totalSteps);
      return { ...o, currentStep: next };
    });
    saveCountdowns({ ...data, [nationId]: { objectives } });
  };

  /** Remove a completed objective */
  const removeObjective = (nationId, objectiveId) => {
    const data = ensureNation(nationId);
    const objectives = (data[nationId]?.objectives || []).filter(o => o.id !== objectiveId);
    saveCountdowns({ ...data, [nationId]: { objectives } });
    toast.success('Objective removed', { icon: '🗑️' });
  };

  /** Add a new objective */
  const addObjective = (nationId) => {
    if (!newName.trim()) return;
    const data = ensureNation(nationId);
    const steps = newType === 'major' ? majorSteps : newSteps;
    const objective = {
      id: `${nationId}-${newType}-${Date.now()}`,
      name: newName.trim(),
      type: newType,
      totalSteps: steps,
      currentStep: steps,
      completed: false,
    };
    const objectives = [...(data[nationId]?.objectives || []), objective];
    saveCountdowns({ ...data, [nationId]: { objectives } });
    toast.success(`New ${newType} objective added for ${nations[nationId]?.name || nationId}`, { icon: '📋' });
    setAddingFor(null);
    setNewName('');
    setNewType('minor');
    setNewSteps(minorRange[0]);
  };

  /** Initialize all nations from static data */
  const initAllNations = () => {
    const data = {};
    nationIds.forEach(id => {
      data[id] = { objectives: initNation(id) };
    });
    saveCountdowns(data);
    toast.success('All nation objectives initialized from starting data', { icon: '🏴' });
  };

  const hasAnyData = Object.keys(allCountdowns).length > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-indigo-600 dark:text-indigo-400 mb-1">
          📋 Objective Countdowns
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Track each nation's progress toward their objectives
        </p>
      </div>

      {/* Init button if no data */}
      {!hasAnyData && (
        <DomainCardButton onClick={initAllNations} variant="primary" className="w-full">
          Initialize Starting Objectives
        </DomainCardButton>
      )}

      {/* Per-Nation Sections */}
      {nationIds.map(nationId => {
        const nation = nations[nationId];
        const objectives = getNationObjectives(nationId);
        if (!hasAnyData && objectives.length === 0) return null;

        return (
          <div key={nationId} className="bg-gray-800 rounded-lg p-3 space-y-2">
            <h4 className="font-bold text-gray-200">{nation?.name || nationId}</h4>

            {objectives.length === 0 && (
              <p className="text-xs text-gray-500 italic">No active objectives</p>
            )}

            {objectives.map(obj => {
              const pct = obj.totalSteps > 0
                ? ((obj.totalSteps - obj.currentStep) / obj.totalSteps) * 100
                : 0;
              const barColor = obj.type === 'major' ? 'bg-yellow-500' : 'bg-sky-500';

              return (
                <div key={obj.id} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className={`font-medium ${obj.completed ? 'text-green-400 line-through' : 'text-gray-300'}`}>
                      {obj.type === 'major' ? '★' : '○'} {obj.name}
                    </span>
                    <span className="text-gray-500">
                      {obj.completed ? 'DONE' : `${obj.currentStep}/${obj.totalSteps}`}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${obj.completed ? 'bg-green-500' : barColor}`}
                      style={{ width: `${obj.completed ? 100 : pct}%` }}
                    />
                  </div>

                  {/* Controls */}
                  <div className="flex gap-2">
                    {!obj.completed && (
                      <>
                        <DomainCardButton
                          onClick={() => tickDown(nationId, obj.id)}
                          variant="primary"
                          className="px-3 py-1"
                        >
                          Tick Down
                        </DomainCardButton>
                        <DomainCardButton
                          onClick={() => tickUp(nationId, obj.id)}
                          variant="secondary"
                          className="px-3 py-1"
                        >
                          Tick Up
                        </DomainCardButton>
                      </>
                    )}
                    {obj.completed && (
                      <DomainCardButton
                        onClick={() => removeObjective(nationId, obj.id)}
                        variant="secondary"
                        className="px-3 py-1"
                      >
                        Remove
                      </DomainCardButton>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Add Objective */}
            {addingFor === nationId ? (
              <div className="bg-gray-700/50 rounded p-2 space-y-2">
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="Objective name..."
                  className="w-full bg-gray-900 text-gray-200 rounded px-2 py-1 text-sm"
                />
                <div className="flex gap-2 items-center text-xs">
                  <label className="text-gray-400">Type:</label>
                  <select
                    value={newType}
                    onChange={e => {
                      setNewType(e.target.value);
                      if (e.target.value === 'major') setNewSteps(majorSteps);
                    }}
                    className="bg-gray-900 text-gray-200 rounded px-2 py-1"
                  >
                    <option value="major" disabled={countActive(nationId, 'major') >= maxActive.major}>
                      Major ({majorSteps} steps)
                    </option>
                    <option value="minor" disabled={countActive(nationId, 'minor') >= maxActive.minor}>
                      Minor
                    </option>
                  </select>
                  {newType === 'minor' && (
                    <>
                      <label className="text-gray-400">Steps:</label>
                      <input
                        type="number"
                        min={minorRange[0]}
                        max={minorRange[1]}
                        value={newSteps}
                        onChange={e => setNewSteps(Number(e.target.value))}
                        className="bg-gray-900 text-gray-200 rounded px-2 py-1 w-14"
                      />
                    </>
                  )}
                </div>
                <div className="flex gap-2">
                  <DomainCardButton onClick={() => addObjective(nationId)} variant="primary" className="px-3 py-1">
                    Add
                  </DomainCardButton>
                  <DomainCardButton onClick={() => setAddingFor(null)} variant="secondary" className="px-3 py-1">
                    Cancel
                  </DomainCardButton>
                </div>
              </div>
            ) : (
              <DomainCardButton
                onClick={() => setAddingFor(nationId)}
                variant="secondary"
                className="w-full px-3 py-1"
              >
                + Add Objective
              </DomainCardButton>
            )}
          </div>
        );
      })}

      <DomainCardButton onClick={onClose} variant="secondary" className="w-full">
        Close
      </DomainCardButton>
    </div>
  );
};

export default ObjectiveCountdownPattern;
